import {
  ChevronDown,
  Facebook,
  Globe,
  Instagram,
  Menu,
  Moon,
  Phone,
  Search,
  Sun,
  User,
  X,
  Youtube,
  ZoomIn, ZoomOut
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAuthDialog } from "../contexts/AuthDialogContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { useZoom } from "../contexts/ZoomContext";
import odooApi, { buildPhoneHref, getDisplayPhoneNumber } from "../services/odooApi";
import FlagIcon from "./FlagIcon";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// TikTok Icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Telegram Icon
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { openAuth } = useAuthDialog();
  
  const { isDarkMode, toggleTheme } = useTheme();
  const { currentLanguage, languages, changeLanguage, t, isRTL } = useLanguage();
  const { currentCurrency, currencies, changeCurrency } = useCurrency();
  const { zoomLevel, zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoom();

  const navLinks = [
    { name: t("nav.home", "Home"), path: "/" },
    { name: t("nav.cars", "Cars"), path: "/cars" },
    { name: t("nav.offers", "Offer"), path: "/offers" },
    { name: t("nav.pricing", "Pricing"), path: "/pricing" },
    { name: t("nav.partners", "Partners"), path: "/partners" },
    { name: t("nav.contact", "Contact"), path: "/contact" },
  ];

  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [config, setConfig] = useState(null);
  const [popularSearches, setPopularSearches] = useState([]);
  const [recommendedSearches, setRecommendedSearches] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchNav = async () => {
      try {
        const [cats, cts, brds, cfg, suggestions] = await Promise.all([
          odooApi.getCategories(),
          odooApi.getCities(),
          odooApi.getBrands(),
          odooApi.getCmsConfig(),
          odooApi.getSearchSuggestions(),
        ]);
        if (!isMounted) return;
        setCategories(cats);
        setCities(cts);
        setBrands(brds);
        setConfig(cfg);
        setPopularSearches(suggestions.popular || []);
        setRecommendedSearches(suggestions.recommended || []);
      } catch (err) {
        console.error("Failed to load nav data:", err);
      }
    };
    fetchNav();
    return () => {
      isMounted = false;
    };
  }, [currentLanguage]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const currentCurr = currencies.find(c => c.code === currentCurrency);

  const closeSearch = () => {
    setSearchQuery("");
    setSearchFocused(false);
    setSearchOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cars?search=${encodeURIComponent(searchQuery.trim())}`);
      closeSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (!suggestion) {
      return;
    }

    if (typeof suggestion === "string") {
      navigate(`/cars?search=${encodeURIComponent(suggestion)}`);
      closeSearch();
      return;
    }

    const params = suggestion.params || {};
    const queryString = new URLSearchParams(params).toString();
    const targetUrl = suggestion.url || (queryString ? `/cars?${queryString}` : "");

    if (targetUrl) {
      if (/^https?:\/\//i.test(targetUrl)) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl);
      }
    } else {
      navigate(`/cars?search=${encodeURIComponent(suggestion.text || suggestion.label || "")}`);
    }
    closeSearch();
  };

  const handleBrandClick = (brandSlug) => {
    navigate(`/brand/${brandSlug}`);
    closeSearch();
  };

  // Filter suggestions based on search query
  const normalizedSearchQuery = searchQuery.toLowerCase();
  const filteredPopular = searchQuery 
    ? popularSearches.filter(s => (s.text || s.label || "").toLowerCase().includes(normalizedSearchQuery))
    : popularSearches.slice(0, 6);

  const filteredBrands = searchQuery && brands.length > 0
    ? brands.filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 4)
    : [];

  const firstName = user?.name?.split(' ')?.[0] || t("profile.account", "Account");
  const userInitial = user?.name?.trim()?.charAt(0)?.toUpperCase() || "V";
  const headerPhoneLabel = getDisplayPhoneNumber(config?.phone);
  const headerPhoneHref = buildPhoneHref(config?.phone);
  const socialLinks = {
    facebook: config?.social?.facebook || "https://facebook.com",
    instagram: config?.social?.instagram || "https://instagram.com",
    tiktok: config?.social?.tiktok || "https://tiktok.com",
    telegram: config?.social?.telegram || "https://t.me",
    youtube: config?.social?.youtube || "https://youtube.com",
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handlePhoneClick = () => {
    odooApi.trackClick({ click_type: "call" });
  };

  return (
    <header className="bg-black/95 dark:bg-gray-950/95 backdrop-blur-md sticky top-0 z-50">
      {/* Top Bar - Social & Utilities */}
      <div className="border-b border-[#D4AF37]/20">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className={`flex items-center justify-between h-10 text-xs ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Social Media Icons */}
            <div className={`hidden md:flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className="text-white/50 uppercase tracking-wider text-[10px]">{t("header.followUs", "Follow Us")}:</span>
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer"
                 className="text-white/60 hover:text-[#1877F2] transition-colors" data-testid="social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                 className="text-white/60 hover:text-[#E4405F] transition-colors" data-testid="social-instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer"
                 className="text-white/60 hover:text-white transition-colors" data-testid="social-tiktok">
                <TikTokIcon className="w-4 h-4" />
              </a>
              <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer"
                 className="text-white/60 hover:text-[#0088cc] transition-colors" data-testid="social-telegram">
                <TelegramIcon className="w-4 h-4" />
              </a>
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                 className="text-white/60 hover:text-[#FF0000] transition-colors" data-testid="social-youtube">
                <Youtube className="w-4 h-4" />
              </a>
              {headerPhoneHref !== '#' && (
                <a
                  href={headerPhoneHref}
                  onClick={handlePhoneClick}
                  className={`inline-flex items-center gap-1.5 text-white/60 hover:text-[#FFD700] transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="social-phone"
                  title={headerPhoneLabel}
                  aria-label={`${t("contact.phone", "Phone")}: ${headerPhoneLabel}`}
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-[11px] font-medium tracking-[0.02em] whitespace-nowrap">{headerPhoneLabel}</span>
                </a>
              )}
            </div>

            {/* Right Side Utilities */}
            <div className={`flex items-center gap-2 md:gap-4 ml-auto ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Zoom Controls */}
              <div className="hidden md:flex items-center gap-1 border-r border-white/10 pr-3">
                <button
                  onClick={zoomOut}
                  disabled={!canZoomOut}
                  className="p-1 text-white/60 hover:text-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="zoom-out-btn"
                  title={t("accessibility.zoomOut", "Zoom Out")}
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-white/50 text-[10px] min-w-[32px] text-center">{zoomLevel}%</span>
                <button
                  onClick={zoomIn}
                  disabled={!canZoomIn}
                  className="p-1 text-white/60 hover:text-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="zoom-in-btn"
                  title={t("accessibility.zoomIn", "Zoom In")}
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Currency Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className="flex items-center gap-1.5 text-white/70 hover:text-[#D4AF37] transition-colors"
                  data-testid="currency-selector"
                >
                  <FlagIcon code={currentCurr?.countryCode || currentCurr?.flag || 'ae'} />
                  <span className="text-[11px] font-medium">{currentCurrency}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black dark:bg-gray-900 border border-[#D4AF37]/30 min-w-[140px]">
                  {currencies.map((curr) => (
                    <DropdownMenuItem 
                      key={curr.code}
                      onClick={() => changeCurrency(curr.code)}
                      className={`flex items-center gap-2 cursor-pointer text-sm ${
                        currentCurrency === curr.code ? 'text-[#FFD700]' : 'text-white/80'
                      } hover:text-[#FFD700] hover:bg-[#D4AF37]/10`}
                    >
                      <FlagIcon code={curr.countryCode || curr.flag} />
                      <span>{curr.code}</span>
                      <span className="text-white/40 ml-auto">{curr.symbol}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  className="flex items-center gap-1.5 text-white/70 hover:text-[#D4AF37] transition-colors"
                  data-testid="language-selector"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span className="text-[11px] font-medium uppercase">{currentLanguage}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black dark:bg-gray-900 border border-[#D4AF37]/30 min-w-[160px]">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-2 cursor-pointer text-sm ${
                        currentLanguage === lang.code ? 'text-[#FFD700]' : 'text-white/80'
                      } hover:text-[#FFD700] hover:bg-[#D4AF37]/10`}
                    >
                      <FlagIcon code={lang.flag} />
                      <span>{lang.native_name || lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-1.5 text-white/60 hover:text-[#D4AF37] transition-colors"
                data-testid="theme-toggle"
                aria-label={isDarkMode ? t("accessibility.switchToLightMode", "Switch to light mode") : t("accessibility.switchToDarkMode", "Switch to dark mode")}
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-[#D4AF37]/30">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className={`flex items-center justify-between h-20 sm:h-24 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Logo - Enlarged */}
            <Link to="/" className="flex items-center group flex-shrink-0" data-testid="header-logo">
              <img
                src="/velaris-brand-mark-attached.png"
                alt={t("accessibility.logoAlt", "Velaris Royal Car Rental")}
                className="h-14 sm:h-18 lg:h-20 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className={`hidden lg:flex items-center gap-6 xl:gap-8 ${isRTL ? 'flex-row-reverse' : ''}`} data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm font-medium tracking-wide uppercase transition-colors ${
                    isActive(link.path)
                      ? "text-[#FFD700]"
                      : "text-white/80 hover:text-[#FFD700]"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Categories Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium tracking-wide uppercase text-white/80 hover:text-[#FFD700] transition-colors" data-testid="categories-dropdown">
                  {t("nav.categories", "Categories")} <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black dark:bg-gray-900 border border-[#D4AF37]/30">
                  {categories.map((cat) => (
                    <DropdownMenuItem key={cat.slug} asChild>
                      <Link
                        to={`/category/${cat.slug}`}
                        className="text-white/80 hover:text-[#FFD700] hover:bg-[#D4AF37]/10 cursor-pointer"
                      >
                        {cat.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Cities Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium tracking-wide uppercase text-white/80 hover:text-[#FFD700] transition-colors" data-testid="cities-dropdown">
                  {t("nav.cities", "Cities")} <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black dark:bg-gray-900 border border-[#D4AF37]/30">
                  {cities.map((city) => (
                    <DropdownMenuItem key={city.slug} asChild>
                      <Link
                        to={`/city/${city.slug}`}
                        className="text-white/80 hover:text-[#FFD700] hover:bg-[#D4AF37]/10 cursor-pointer"
                      >
                        {city.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Right Side - Search, Contact & CTA */}
            <div className={`hidden lg:flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  className="flex items-center gap-3 rounded-full border border-[#D4AF37]/30 bg-white/[0.03] px-2 py-2 pr-4 text-white/80 transition-colors hover:border-[#D4AF37] hover:text-[#FFD700]"
                  data-testid="header-profile-link"
                >
                  <Avatar className="h-10 w-10 border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37] to-[#8C6A12]">
                    <AvatarFallback className="bg-transparent text-sm font-semibold text-black">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                      {t("nav.account", "Account")}
                    </p>
                    <p className="text-sm font-semibold leading-none">{firstName}</p>
                  </div>
                </Link>
              ) : (
                <button
                  onClick={() => openAuth("login")}
                  className="px-4 py-2 border border-[#D4AF37]/30 rounded-full text-sm font-semibold text-white/80 hover:text-[#FFD700] hover:border-[#D4AF37] transition-colors"
                  data-testid="header-signin-btn"
                >
                  {t("auth.signIn", "Sign In")}
                </button>
              )}

              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-10 h-10 rounded-full border border-[#D4AF37]/30 flex items-center justify-center text-white/80 hover:text-[#FFD700] hover:border-[#D4AF37] transition-colors"
                data-testid="search-toggle"
              >
                <Search className="w-5 h-5" />
              </button>
              <Link
                to="/cars"
                className="btn-gold px-6 py-2.5 text-black font-bold text-sm uppercase tracking-wider rounded-md"
                data-testid="header-cta"
              >
                {t("btn.browse", "Browse Cars")}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className={`lg:hidden flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Mobile Search */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="text-white p-2"
                data-testid="mobile-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>

              <button
                className="text-white p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar - Expandable */}
      {searchOpen && (
        <div className="bg-black/98 border-b border-[#D4AF37]/30 py-4">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10">
            <div ref={searchRef} className="relative">
              <form onSubmit={handleSearch} className="relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 ${isRTL ? 'right-4' : 'left-4'}`} />
                <input
                  type="text"
                  dir={isRTL ? "rtl" : "ltr"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  placeholder={t("search.placeholder", "Search by brand, model, or category...")}
                  className={`w-full bg-white/5 border border-[#D4AF37]/30 rounded-lg py-3 text-white placeholder:text-white/40 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/50 transition-all ${
                    isRTL ? "pr-12 pl-28 text-right" : "pl-12 pr-28 text-left"
                  }`}
                  data-testid="search-input"
                />
                <button
                  type="submit"
                  className={`absolute top-1/2 -translate-y-1/2 bg-[#D4AF37] text-black px-4 py-1.5 rounded text-sm font-semibold hover:bg-[#FFD700] transition-colors ${
                    isRTL ? "left-2" : "right-2"
                  }`}
                >
                  {t("btn.search", "Search")}
                </button>
              </form>

              {/* Search Suggestions Dropdown */}
              {searchFocused && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-[#D4AF37]/30 rounded-lg overflow-hidden shadow-2xl z-50">
                  {/* Popular Searches */}
                  {filteredPopular.length > 0 && (
                    <div className="p-4 border-b border-white/10">
                      <h4 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3">
                        {t("search.popular", "Popular Searches")}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {filteredPopular.map((item, idx) => (
                          <button
                            key={item.id || item.text || idx}
                            onClick={() => handleSuggestionClick(item)}
                            className="px-3 py-1.5 bg-white/5 hover:bg-[#D4AF37]/20 border border-white/10 hover:border-[#D4AF37]/50 rounded-full text-sm text-white/70 hover:text-white transition-all"
                          >
                            {item.text || item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommended */}
                  {recommendedSearches.length > 0 && (
                    <div className="p-4">
                      <h4 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3">
                        {t("search.recommended", "Recommended")}
                      </h4>
                      <div className="space-y-2">
                        {recommendedSearches.map((item, idx) => (
                          <button
                            key={item.id || item.text || idx}
                            onClick={() => handleSuggestionClick(item)}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg text-left transition-colors group"
                          >
                            <Search className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37]" />
                            <span className="text-white/70 group-hover:text-white">{item.text || item.label}</span>
                            <span className="ml-auto text-[10px] uppercase text-white/30 bg-white/5 px-2 py-0.5 rounded">
                              {item.type || item.typeLabel}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Brand matches if searching */}
                  {filteredBrands.length > 0 && (
                    <div className="p-4 border-t border-white/10">
                      <h4 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3">
                        {t("search.brands", "Brands")}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {filteredBrands.map((brand) => (
                          <button
                            key={brand.slug}
                            onClick={() => handleBrandClick(brand.slug)}
                            className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-[#D4AF37]/20 rounded-lg transition-colors"
                          >
                            {brand.logo && (
                              <img src={brand.logo} alt={brand.name} className="w-6 h-6 object-contain" />
                            )}
                            <span className="text-sm text-white/80">{brand.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-black dark:bg-gray-950 border-t border-[#D4AF37]/30" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {/* Mobile Social Icons */}
            <div className="flex items-center justify-center gap-4 pb-4 border-b border-white/10">
              <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#1877F2]">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#E4405F]">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white">
                <TikTokIcon className="w-5 h-5" />
              </a>
              <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#0088cc]">
                <TelegramIcon className="w-5 h-5" />
              </a>
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#FF0000]">
                <Youtube className="w-5 h-5" />
              </a>
              {headerPhoneHref !== '#' && (
                <a
                  href={headerPhoneHref}
                  onClick={handlePhoneClick}
                  className="text-white/60 hover:text-[#FFD700]"
                  title={headerPhoneLabel}
                  aria-label={`${t("contact.phone", "Phone")}: ${headerPhoneLabel}`}
                >
                  <Phone className="w-5 h-5" />
                </a>
              )}
            </div>

            {/* Mobile Currency & Language */}
            <div className="flex items-center justify-center gap-4 pb-4 border-b border-white/10">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded text-white/70">
                  <FlagIcon code={currentCurr?.countryCode || currentCurr?.flag || 'ae'} />
                  <span className="text-sm">{currentCurrency}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black border border-[#D4AF37]/30">
                  {currencies.map((curr) => (
                    <DropdownMenuItem 
                      key={curr.code}
                      onClick={() => changeCurrency(curr.code)}
                      className={`flex items-center gap-2 cursor-pointer ${
                        currentCurrency === curr.code ? 'text-[#FFD700]' : 'text-white/80'
                      }`}
                    >
                      <FlagIcon code={curr.countryCode || curr.flag} />
                      <span>{curr.code}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded text-white/70">
                  <Globe className="w-4 h-4" />
                  <span className="text-sm uppercase">{currentLanguage}</span>
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-black border border-[#D4AF37]/30">
                  {languages.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-2 cursor-pointer ${
                        currentLanguage === lang.code ? 'text-[#FFD700]' : 'text-white/80'
                      }`}
                    >
                      <FlagIcon code={lang.flag} />
                      <span>{lang.native_name || lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <button onClick={toggleTheme} className="p-2 bg-white/5 rounded text-white/70">
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-2 text-sm font-medium tracking-wide uppercase ${
                  isActive(link.path) ? "text-[#FFD700]" : "text-white/80"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-[#D4AF37]/30">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">{t("nav.categories", "Categories")}</p>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm text-white/60 hover:text-[#FFD700]"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="pt-4 border-t border-[#D4AF37]/30">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">{t("nav.cities", "Cities")}</p>
              {cities.map((city) => (
                <Link
                  key={city.slug}
                  to={`/city/${city.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-sm text-white/60 hover:text-[#FFD700]"
                >
                  {city.name}
                </Link>
              ))}
            </div>
            <div className="pt-4">
              {isAuthenticated ? (
                <div className="mb-4 space-y-3">
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 rounded-2xl border border-[#D4AF37]/30 bg-white/[0.03] px-4 py-4 text-white/80"
                    data-testid="mobile-profile-link"
                  >
                    <Avatar className="h-11 w-11 border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37] to-[#8C6A12]">
                      <AvatarFallback className="bg-transparent text-sm font-semibold text-black">
                        {userInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                        {t("nav.account", "Account")}
                      </p>
                      <p className="font-semibold text-white">{firstName}</p>
                    </div>
                    <User className="w-4 h-4 text-[#D4AF37]" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center justify-center rounded-lg border border-red-500/30 px-4 py-3 text-red-400"
                    data-testid="mobile-logout-btn"
                  >
                    {t("auth.signOut", "Sign Out")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    openAuth("login");
                  }}
                  className="mb-4 block w-full rounded-lg border border-[#D4AF37]/30 px-4 py-3 text-center text-sm font-semibold text-white/80"
                  data-testid="mobile-signin-btn"
                >
                  {t("auth.signIn", "Sign In")}
                </button>
              )}

              <Link
                to="/cars"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full btn-gold px-6 py-3 text-center text-black font-bold text-sm uppercase tracking-wider rounded-md"
              >
                {t("btn.browse", "Browse Cars")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
